import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Paper from "@material-ui/core/Paper";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import _ from "lodash";

import BrowserContent from "./subsections/Browser";
import InterpolatorContent from "./subsections/Interpolator";
import State from "./components/State";

const LIST_TRAJ_QUERY = gql`
  query {
    listTrajectories(limit: 100) {
      items {
        id
        version
        CHI
        PHI
        count
      }
    }
  }
`;

const STATE_QUERY = gql`
  query getState($trajectoryId: String!, $timestep: Int!) {
    getState(trajectoryId: $trajectoryId, timestep: $timestep) {
      trajectoryId
      timestep
      binaryImage
      encoding
    }
  }
`;

const styles = theme => ({
  paper: {
    maxWidth: 936,
    margin: "auto",
    overflow: "hidden"
  },
  searchBar: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
  },
  searchInput: {
    fontSize: theme.typography.fontSize
  },
  block: {
    display: "block"
  },
  addUser: {
    marginRight: theme.spacing(1)
  },
  contentWrapper: {
    margin: "40px 16px"
  }
});

function DataContent(props) {
  const { classes, client, activeSubsection } = props;
  const [savedStates, setSavedStates] = React.useState(() => {
    try {
      const item = localStorage.getItem("savedStates");
      const states = JSON.parse(item || []);
      return _.filter(states, o => o.encoding !== undefined);
    } catch (e) {
      return [];
    }
  });
  let { loading, error, data } = useQuery(LIST_TRAJ_QUERY);
  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error :</p>;

  const addSavedState = async state => {
    for (let savedState of savedStates) {
      // check that we haven't already added the state
      if (
        savedState.trajectoryId === state.trajectoryId &&
        savedState.version === state.version &&
        savedState.timestep === state.timestep
      ) {
        // if we have, then return
        return;
      }
    }
    let newSavedStates = _.uniq(savedStates.concat([state])); // add the state to our saved states
    while (newSavedStates.length > 2) {
      // if we have more than two saved states,
      // push the oldest ones out
      newSavedStates.shift();
    }
    setSavedStates(newSavedStates);
    if (state.encoding === undefined) {
      // if the provided state data doesn't include an encoding,
      // we have to refetch the state with it.
      // The 'updateSavedState' function will bring us back here,
      // except with an encoding.
      client
        .query({
          query: STATE_QUERY,
          variables: {
            trajectoryId: state.trajectoryId,
            timestep: state.timestep
          }
        })
        .then(result => updateSavedState(result.data.getState))
        .catch(err => console.error(err));
    } else {
      // if we have an encoding, update the saved states in localStorage
      localStorage.setItem("savedStates", JSON.stringify(newSavedStates));
    }
  };

  const updateSavedState = state => {
    removeSavedState(state);
    addSavedState(state);
  };

  const removeSavedState = state => {
    const newSavedStates = savedStates.filter(
      savedState =>
        !(
          savedState.trajectoryId === state.trajectoryId &&
          savedState.version === state.version &&
          savedState.timestep === state.timestep
        )
    );
    setSavedStates(newSavedStates);
    localStorage.setItem("savedStates", JSON.stringify(newSavedStates));
  };

  let pageContent;
  switch (activeSubsection) {
    case 0: {
      pageContent = (
        <BrowserContent
          classes={classes}
          client={client}
          trajectories={data.listTrajectories.items}
          addSavedState={addSavedState}
        />
      );
      break;
    }
    case 1: {
      pageContent = (
        <InterpolatorContent client={client} savedStates={savedStates} />
      );
      break;
    }
    default: {
      pageContent = <div>Select Subsection in Header</div>;
    }
  }

  return (
    <Paper className={classes.paper}>
      {pageContent}
      <AppBar
        className={classes.searchBar}
        position="static"
        color="default"
        elevation={0}
        style={{ minHeight: "50px" }}
      >
        {savedStates.length === 0 ? (
          <Typography
            color="textSecondary"
            align="center"
            style={{ margin: "auto" }}
          >
            Select two states for interpolation.
          </Typography>
        ) : (
          <Toolbar
            style={{
              maxHeight: "200px",
              padding: "10px",
              overflow: "auto",
              overflowX: "scroll",
              overflowY: "hidden",
              whiteSpace: "nowrap",
              display: "flex",
              justifyContent: "center"
            }}
          >
            {savedStates.map(state => (
              <State
                key={`${state.trajectoryId}-${state.timestep}`}
                state={state}
                deleteFunc={() => removeSavedState(state)}
              />
            ))}
          </Toolbar>
        )}
      </AppBar>
    </Paper>
  );
}

DataContent.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(DataContent);



// WEBPACK FOOTER //
// ./src/sections/data/Content.js